import { useSearchParams } from 'react-router-dom';
import { SegmentedControl, type TabItem } from '../../components/ui/Tabs';
import { MeasurementDashboard } from '../Dashboard/MeasurementDashboard';
import { SearchVisibilityView } from './search-visibility';
import { RANGE_PARAM } from './shared';

type Source = 'google' | 'ai';

/**
 * Marketing → Visibility: where we rank on Google (Semrush) and what AI
 * assistants say about us (Peec), behind one toggle. The AI side keeps its
 * own feature gate (admin opt-in), so the toggle only appears for viewers who
 * can see both.
 */
export function VisibilityView({ canGoogle, canAi }: { canGoogle: boolean; canAi: boolean }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('view');
  // Old AI Visibility deep links carry `?page=` (prompts, sources…): open them on the AI side.
  const wantsAi = requested === 'ai' || (requested === null && searchParams.has('page'));
  const source: Source = canAi && (wantsAi || !canGoogle) ? 'ai' : 'google';

  const options: TabItem<Source>[] = [
    ...(canGoogle ? [{ id: 'google' as const, label: 'Google' }] : []),
    ...(canAi ? [{ id: 'ai' as const, label: 'AI assistants' }] : []),
  ];

  // Each side owns different params (market, AI sub-page), so only the shared period carries across.
  const setSource = (next: Source) => {
    const kept = Object.fromEntries(
      [RANGE_PARAM, 'cstart', 'cend'].flatMap((k) => {
        const v = searchParams.get(k);
        return v ? [[k, v]] : [];
      }),
    );
    setSearchParams({ ...(next === 'ai' ? { view: 'ai' } : {}), ...kept });
  };

  const toggle = options.length > 1 ? (
    <SegmentedControl ariaLabel="Search surface" options={options} value={source} onChange={setSource} />
  ) : null;

  if (source === 'ai') {
    return (
      <div className="space-y-6">
        {toggle}
        <MeasurementDashboard />
      </div>
    );
  }
  return <SearchVisibilityView toolbarStart={toggle} />;
}
